/**
 * Agreements Service
 * Handles user agreement signing and retrieval operations.
 * 
 * Based on: APIdocs/agreements-api.openapi.json
 * 
 * User endpoints (base: /v2/agreements):
 * - POST / - Create/upsert an agreement
 * - GET / - Get all signed agreements for authenticated user
 * - GET /name/{agreementName} - Get agreement by name
 * - GET /{id} - Get agreement by ID
 * - PUT /{id} - Update agreement by ID
 * 
 * Admin endpoints (base: /v2/admin):
 * - GET /users/{id}/agreements - Get all agreements for a specific user
 * - DELETE /users/{id}/agreements - Delete all agreements for a user
 * - DELETE /users/{id}/agreements/{agreementName} - Delete specific agreement
 */

import httpService, { type HttpError } from "./httpService";

export type AgreementSignatureStatus = "signed" | "unsigned";

/**
 * Browser/request metadata captured when agreement was created
 */
export interface AgreementIdentifiers {
  ipAddress?: string;
  userAgent?: string;
  browser?: string;
  os?: string;
  deviceType?: "Desktop" | "Mobile" | "Tablet";
  acceptLanguage?: string;
  acceptEncoding?: string;
  referer?: string;
  origin?: string;
  host?: string;
}

/**
 * Agreement record from the backend
 */
export interface Agreement {
  id: number;
  userId: string;
  agreementName: string;
  agreementDocument?: string | null;
  identifiers?: AgreementIdentifiers | null;
  data?: Record<string, unknown> | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

/**
 * Request payload for creating/upserting an agreement
 */
export interface CreateAgreementRequest {
  /** Agreement name - letters, numbers, and hyphens only */
  agreementName: string;
  /** Optional document reference URL or path */
  agreementDocument?: string | null;
  /** Additional data to store with the agreement */
  data?: Record<string, unknown> | null;
}

/**
 * Response from creating an agreement
 */
export interface CreateAgreementResponse {
  message: string;
  userAgreement: Agreement;
  isUpdate: boolean;
}

/**
 * Response from fetching user's agreements
 */
export interface GetAgreementsResponse {
  count: number;
  userAgreements: Agreement[];
}

/**
 * Response from admin fetching user's agreements
 */
export interface AdminGetAgreementsResponse {
  message: string;
  userId: string;
  count: number;
  userAgreements: Agreement[];
}

/**
 * Response from admin deleting all agreements
 */
export interface AdminDeleteAllAgreementsResponse {
  message: string;
  userId: string;
  deletedCount: number;
}

/**
 * Response from admin deleting a specific agreement
 */
export interface AdminDeleteAgreementResponse {
  message: string;
  userId: string;
  agreementName: string;
  deletedAgreementId: number;
}

class AgreementsService {
  private readonly userBasePath = "/v2/agreements";
  private readonly adminBasePath = "/v2/admin/users";

  // ============================================
  // User Operations
  // ============================================

  /**
   * Create or update (upsert) an agreement for the authenticated user
   */
  async signAgreement(request: CreateAgreementRequest): Promise<Agreement> {
    const response = await httpService.post<CreateAgreementResponse>(
      this.userBasePath,
      request
    );
    return response.data.userAgreement;
  }

  /**
   * Get all signed agreements for the authenticated user
   */
  async getMyAgreements(): Promise<Agreement[]> {
    const response = await httpService.get<GetAgreementsResponse>(
      this.userBasePath
    );
    return response.data.userAgreements;
  }

  /**
   * Check if a specific agreement has been signed by the authenticated user.
   * Uses HEAD (existence check) so we don't pay for a body we'd discard.
   */
  async hasSignedAgreement(agreementName: string): Promise<boolean> {
    try {
      await httpService.head(
        `${this.userBasePath}/name/${encodeURIComponent(agreementName)}`
      );
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Verify an agreement without conflating absence with an unavailable API.
   * Alpha's legal gate uses this strict contract; legacy callers keep the
   * boolean behavior of hasSignedAgreement above.
   */
  async getAgreementSignatureStatus(
    agreementName: string
  ): Promise<AgreementSignatureStatus> {
    try {
      await httpService.head(
        `${this.userBasePath}/name/${encodeURIComponent(agreementName)}`
      );
      return "signed";
    } catch (error) {
      const httpError = error as HttpError;
      const status = httpError.status ?? httpError.response?.status;
      if (status === 404) return "unsigned";
      throw error;
    }
  }

  /**
   * Get a specific agreement by name for the authenticated user
   */
  async getAgreementByName(agreementName: string): Promise<Agreement | null> {
    try {
      const response = await httpService.get<Agreement>(
        `${this.userBasePath}/name/${encodeURIComponent(agreementName)}`
      );
      return response.data;
    } catch {
      return null;
    }
  }

  /**
   * Get a specific agreement by ID for the authenticated user
   */
  async getAgreementById(id: number): Promise<Agreement | null> {
    try {
      const response = await httpService.get<Agreement>(
        `${this.userBasePath}/${id}`
      );
      return response.data;
    } catch {
      return null;
    }
  }

  // ============================================
  // Admin Operations
  // ============================================

  /**
   * Get all agreements for a specific user (admin only)
   */
  async getUserAgreements(userId: string): Promise<Agreement[]> {
    const response = await httpService.get<AdminGetAgreementsResponse>(
      `${this.adminBasePath}/${userId}/agreements`
    );
    return response.data.userAgreements;
  }

  /**
   * Delete all agreements for a user (admin only)
   */
  async deleteAllUserAgreements(userId: string): Promise<number> {
    const response = await httpService.delete<AdminDeleteAllAgreementsResponse>(
      `${this.adminBasePath}/${userId}/agreements`
    );
    return response.data.deletedCount;
  }

  /**
   * Delete a specific agreement for a user by name (admin only)
   */
  async deleteUserAgreement(
    userId: string,
    agreementName: string
  ): Promise<void> {
    await httpService.delete<AdminDeleteAgreementResponse>(
      `${this.adminBasePath}/${userId}/agreements/${encodeURIComponent(agreementName)}`
    );
  }
}

export const agreementsService = new AgreementsService();
export default agreementsService;
